import { useState, useEffect } from 'react';
import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import { PersonaPicker } from '@sdk/components/forms/personaPickers/PersonaPicker/PersonaPicker';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import useAuth from '@sdk/authentication/useAuth';
import { UpdateLaborCostSettings } from '@sdk/labor/types';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

type AccessUserType = {
  id: number;
  type: string;
  color?: string;
  fullname?: string;
  name?: string;
};

type Props = {
  users: AuthUserType[];
  access: AccessUserType[];
  onLaborCostSettingsChange: UpdateLaborCostSettings;
};

const LaborCostAccess: React.FC<React.PropsWithChildren<Props>> = ({
  users,
  access,
  onLaborCostSettingsChange,
}) => {
  const [admins, setAdmins] = useState(() => prepareOptions(users.filter((p) => p.isAdmin)));
  const [managers, setManagers] = useState(() =>
    prepareOptions(users.filter((p) => p.isManager && !p.isAdmin))
  );
  const [selectedUsers, setSelectedUsers] = useState<PersonaPickerOption[]>([
    ...admins,
    ...prepareOptions(access),
  ]);
  const { currentUser } = useAuth();
  const adminsIds = admins.map((i) => i.id);

  useEffect(() => {
    setAdmins(prepareOptions(users.filter((p) => p.isAdmin)));
    setManagers(prepareOptions(users.filter((p) => p.isManager && !p.isAdmin)));
    setSelectedUsers([...admins, ...prepareOptions(access)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, access]);

  return (
    <>
      <Column pBottom={20}>
        <Text14 themeColor={ThemeColorType.gray}>
          {gettext('All admins have access by default to labor cost, reports, and wages.')}
        </Text14>
        <Text14 themeColor={ThemeColorType.gray}>
          {gettext('Managers can be given access below:')}
        </Text14>
      </Column>

      <PersonaPicker
        onSelectedItemsChange={(value: PersonaPickerOption[]): void => {
          setSelectedUsers(value);
          onLaborCostSettingsChange({
            access: value
              .filter((i) => !adminsIds.includes(i.id))
              .map((persona) => ({
                id: Number(persona.id),
                type: 'user',
                fullname: persona.title,
                color: persona.color,
              })),
          });
        }}
        options={managers}
        selectedItems={selectedUsers}
        placeholder={gettext('Add managers')}
        nonDeletableIds={adminsIds}
        disabled={!currentUser.isAdmin}
      />
    </>
  );
};

type PersonaForMapper = {
  id: number;
  fullname?: string;
  color?: string;
  location?: string;
  position?: string;
};
const prepareOptions = (personas: PersonaForMapper[]): PersonaPickerOption[] => {
  return personas.map((option) => ({
    id: option.id,
    title: option.fullname || '',
    color: option.color,
    subtitle: option?.location,
    highlight: option?.position,
  }));
};

export default LaborCostAccess;
